/**
 * Private route file
 *
 * @package    src/shared
 * @author     DongTuring <dong@turing.com>
 * @copyright  2018 Turing Company
 * @license    Turing License
 * @version    2.0
 * @link       https://turing.ly
 */

import React, { useEffect, useState } from "react";
import { Route, Redirect } from "react-router-dom";
import { Loader } from 'react-overlay-loader'
import Announcement from "../../shared/Announcement";
import { isMobile } from "react-device-detect";
// import VisitOnDesktop from '../components/LandingPage/VisitOnDesktop';
import MobileHome from "../legacy_components/UI_V2_Components/home/HomeV1/mobile/MobileHome";
import { PageConstant } from "../constants";
import { getOpenGBFlags } from "../services/getOpenFlag";
import OpenFeatureFlags from "../constants/OpenFeatureFlags";
import { getMobilePageVersion } from "../legacy_components/util/getMobilePageVersion";
import MobileTestBlockerV2 from "../legacy_components/UI_V2_Components/home/HomeV1/mobile/MobileTestBlockerV2";
import { checkJWTExpiration } from "../legacy_components/util/checkJWTExpiration";

/**
 * Function that redirects to the page if user is authenticated
 *
 * @author  DongTuring <dong@turing.com>
 * @param   component
 * @param   authenticated
 * @return
 */
export default function PrivateRoute({
  component: Component,
  path,
  authenticated = "false",
  redirect,
  redirectPath,
  canAccessPage,
  allowInMobile = false,
  ...rest
}) {
    const [flags, setFlags] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    // This check and flag fetch is done here because direct apply is a private route
    useEffect(() => {
      if (path.includes('direct-apply')) {
        setIsLoading(true);
        const fetchFlags = async () => {
          const flagsData = await getOpenGBFlags([OpenFeatureFlags.job_from_website_to_dev_portal_code]);
          setFlags(flagsData?.result);
          setIsLoading(false);
        };
        fetchFlags();
      } else {
        setIsLoading(false);
      }
    }, [path]);
    // Check if user should be redirected to the signup page based on a local storage reference or URL path
    const shouldRedirectToSignup = Boolean(localStorage.getItem('ref') || path.includes('direct-apply'));
    // Check Redirection logic for Direct apply route
    const redirectURL = shouldRedirectToSignup ? '/signup' : '/login';

    if (path.includes('direct-apply') && isLoading) {
      // Display a loading spinner while waiting for the flags
      return <Loader fullPage loading={true} />;
    }
  
    if (path.includes('direct-apply') && !isLoading) {
      const jobCode = window.location.pathname.split('/').pop();
      const flag = flags[OpenFeatureFlags.job_from_website_to_dev_portal_code];
      if (flag === true || (Array.isArray(flag) && flag?.indexOf(jobCode) >= 0)) {
        const queryParams = new URLSearchParams(window.location.search);
        const token = queryParams.get('token');
        if (token && checkJWTExpiration(token)) {
          queryParams.delete('token'); // Delete token if it is expired
        }
        queryParams.set('jobCode', jobCode);
        const search = `?${queryParams.toString()}`;
        return <Redirect to={{
          pathname: PageConstant.NEW_FLOW_JOB_HOME,
          search
        }} />;
      }
    }

    if (authenticated && typeof canAccessPage === 'function' && !canAccessPage()) {
      return <Redirect to={redirectPath} />
    }

  const mobileVersion = getMobilePageVersion(path);

  return (
    <Route
      {...rest}
      render={props =>
        (isMobile && !allowInMobile) ? (
          mobileVersion === 'v2' ? <MobileTestBlockerV2 /> : <MobileHome />
        ) : authenticated === "true" && Component ? (
          <>
            <Announcement />
            <Component {...props} {...rest} />
          </>
        ) : authenticated === "true" && redirect ? (
          <Redirect to={redirectPath} />
        ) : authenticated === "false"? (
          <Redirect to= {redirectURL + "?redirect=" + encodeURIComponent(window.location.pathname + window.location.search.replace("?", "&"))} />
        ) : (
          <Redirect to="/" />
        )
      }
    />
  );
}
